<script setup lang="ts">
const config = useRuntimeConfig();
const apiBase = config.public.apiBase;
const formStore = useFormStore();

const props = defineProps<{
  formData: UmbracoFormData;
}>();

const requiredFields = computed(() => {
  const pages = props.formData.pages ?? [];
  return pages
    .flatMap((page) => page.fieldsets || [])
    .flatMap((fieldset) => fieldset.columns || [])
    .flatMap((column) => column.fields || [])
    .filter((field) => field.required && field.alias)
    .map((field) => field.alias);
});

const isFormValid = computed(() => {
  return requiredFields.value.every((fieldAlias) => {
    const value = formStore.fieldValues[fieldAlias];

    if (typeof value === 'boolean') {
      return value === true;
    }

    if (typeof value === 'string') {
      return value.trim() !== '';
    }

    return value !== null && value !== undefined;
  });
});

function submitSuccessful() {
  if (props.formData.gotoPageOnSubmit) {
    navigateTo(handleRelativeUrl(props.formData.gotoPageOnSubmit));
  }

  formStore.isDisabled = false;
  formStore.isSubmitted = true;
}

async function submitForm(event: Event) {
  event.preventDefault();

  formStore.isSubmitted = false;
  formStore.isDisabled = true;

  const form = event.target as HTMLFormElement;
  const formDataEntries = new FormData(form);
  const filteredFormData = new FormData();

  for (const [key, value] of formDataEntries.entries()) {
    const isValidString = typeof value === 'string' && value.trim() !== '';
    const isValidNonString = typeof value !== 'string' && value !== null && value !== undefined;

    if (isValidString || isValidNonString) {
      filteredFormData.append(key, value);
    }
  }

  const res = await $fetch.raw(`${apiBase}/api/forms/${props.formData._id}/entries`, {
    method: 'POST',
    body: filteredFormData
  });

  if (res.ok) {
    submitSuccessful();
  } else {
    const loggingService = useLoggingService();
    loggingService.error('Error submitting form');
  }
}
</script>

<template>
  <form
    v-if="formData.pages.length > 0"
    :id="props.formData._id"
    class="form"
    @submit="submitForm"
  >
    <PartialsFormSimpleForm
      :form-data="formData"
      :is-form-valid="isFormValid"
    />
  </form>
</template>
